import { FC, useMemo, useState } from 'react';
import { Button, Flex, SimpleGrid } from '@chakra-ui/react';

import { useAlert, useApi } from '@app/hooks';
import { SelectField, SwitchField, TextField } from '@app/components/common';
import NumberField from '@app/components/common/number-field';
import { Sku } from '@app/api/models/sku';

import { PendingPhoto, SkuPhotosGallery, uploadPendingSkuPhotos } from './photos';
import { SkuLookups } from './types';

type SkuDraft = Partial<Sku>;

const toEntity = (id: number | undefined, name: string | undefined) =>
  id ? { id, name: name || '' } : undefined;

export const SkuForm: FC<{
  initial?: Partial<Sku>;
  lookups: SkuLookups;
  onSubmit: () => void;
  onCancel: () => void;
}> = ({ initial, lookups, onSubmit, onCancel }) => {
  const api = useApi();
  const { error, success } = useAlert();
  const [loading, setLoading] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [draft, setDraft] = useState<SkuDraft>(
    initial || { is_active: true, is_available_for_order: true, is_top: false }
  );

  const subcategoryOptions = useMemo(
    () => lookups.subcategories.filter((item) => item.category_id === draft.category_id),
    [lookups.subcategories, draft.category_id]
  );

  const patch = (value: SkuDraft) => setDraft((prev) => ({ ...prev, ...value }));

  const onSave = async () => {
    if (!draft.title) {
      error('Введите наименование');
      return;
    }
    if (!draft.brand) {
      error('Введите бренд');
      return;
    }
    if (!draft.category_id) {
      error('Выберите категорию');
      return;
    }
    if (!draft.subcategory_id) {
      error('Выберите подкатегорию');
      return;
    }
    if (!draft.unit_id) {
      error('Выберите единицу измерения');
      return;
    }

    const payload = {
      code: draft.code,
      erp_code: draft.erp_code,
      uniq_code: draft.uniq_code,
      bar_code: draft.bar_code,
      ean_code: draft.ean_code,
      trade_mark: draft.trade_mark,
      brand: draft.brand,
      title: draft.title,
      title_en: draft.title_en,
      category_id: draft.category_id,
      subcategory_id: draft.subcategory_id,
      material: draft.material,
      taste: draft.taste,
      weight: draft.weight,
      volume: draft.volume,
      unit_id: draft.unit_id,
      number_in_package: draft.number_in_package ?? 1,
      is_available_for_order: draft.is_available_for_order ?? false,
      is_top: draft.is_top ?? false,
      sort_order: draft.sort_order ?? 0,
      status: (draft.is_active ? 'active' : 'inactive') as 'active' | 'inactive',
      is_active: draft.is_active ?? true,
    };

    setLoading(true);
    try {
      if (initial?.id) {
        const response = await api.sku.updateSku({ ...payload, pid: initial.id });
        if (response?.code === 200) {
          success('SKU обновлён');
          onSubmit();
        }
      } else {
        const response = await api.sku.createSku(payload);
        if (response?.code === 200) {
          const createdId = response.data?.id;
          if (createdId && pendingPhotos.length > 0) {
            await uploadPendingSkuPhotos(api, createdId, pendingPhotos);
          }
          success('SKU создан');
          onSubmit();
        }
      }
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex gap={3} flexDirection="column">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <TextField
          id="title"
          label="Наименование"
          required
          value={draft.title || ''}
          onChange={(value) => patch({ title: value })}
        />
        <TextField
          id="brand"
          label="Бренд"
          required
          value={draft.brand || ''}
          onChange={(value) => patch({ brand: value })}
        />
        <SelectField
          id="category_id"
          label="Категория"
          required
          options={lookups.categories}
          value={toEntity(draft.category_id, lookups.categoriesById[draft.category_id])}
          onChange={(option) =>
            patch({
              category_id: option ? Number(option.id) : undefined,
              subcategory_id: undefined,
            })
          }
        />
        <SelectField
          id="subcategory_id"
          label="Подкатегория"
          required
          disabled={!draft.category_id}
          options={subcategoryOptions}
          value={toEntity(draft.subcategory_id, lookups.subcategoriesById[draft.subcategory_id])}
          onChange={(option) => patch({ subcategory_id: option ? Number(option.id) : undefined })}
        />
        <SelectField
          id="unit_id"
          label="Единица измерения"
          required
          options={lookups.units}
          value={toEntity(draft.unit_id, lookups.unitsById[draft.unit_id])}
          onChange={(option) => patch({ unit_id: option ? Number(option.id) : undefined })}
        />
        <TextField
          id="code"
          label="Код"
          value={draft.code || ''}
          onChange={(value) => patch({ code: value })}
        />
        <TextField
          id="erp_code"
          label="Код ERP"
          value={draft.erp_code || ''}
          onChange={(value) => patch({ erp_code: value })}
        />
        <TextField
          id="bar_code"
          label="Штрихкод"
          value={draft.bar_code || ''}
          onChange={(value) => patch({ bar_code: value })}
        />
        <NumberField
          id="weight"
          label="Вес"
          value={draft.weight ?? 0}
          onChange={(value) => patch({ weight: value })}
        />
        <NumberField
          id="volume"
          label="Объём"
          value={draft.volume ?? 0}
          onChange={(value) => patch({ volume: value })}
        />
        <NumberField
          id="number_in_package"
          label="Количество в упаковке"
          value={draft.number_in_package ?? 1}
          onChange={(value) => patch({ number_in_package: value })}
        />
        <NumberField
          id="sort_order"
          label="Порядок сортировки"
          value={draft.sort_order ?? 0}
          onChange={(value) => patch({ sort_order: value })}
        />
      </SimpleGrid>
      <Flex gap={6} wrap="wrap">
        <SwitchField
          id="is_available_for_order"
          label="Доступен для заказа"
          value={draft.is_available_for_order}
          onChange={(value) => patch({ is_available_for_order: value })}
        />
        <SwitchField
          id="is_top"
          label="Топовый"
          value={draft.is_top}
          onChange={(value) => patch({ is_top: value })}
        />
        <SwitchField
          id="is_active"
          label="Активен"
          value={draft.is_active}
          onChange={(value) => patch({ is_active: value })}
        />
      </Flex>
      <SkuPhotosGallery
        skuId={initial?.id}
        pending={pendingPhotos}
        onPendingChange={setPendingPhotos}
      />
      <Flex justifyContent="flex-end" gap={2}>
        <Button variant="outline" colorScheme="gray" onClick={onCancel} isDisabled={loading}>
          Отмена
        </Button>
        <Button variant="solid" colorScheme="brand" onClick={onSave} isLoading={loading}>
          Сохранить
        </Button>
      </Flex>
    </Flex>
  );
};

export default SkuForm;
